import { apiClient } from './axios';

import type { ScenarioJudgeOutput } from '@repo/shared-types';

export type ReplayStatus =
  | 'pending'
  | 'running'
  | 'judging'
  | 'judged'
  | 'failed'
  | 'cancelled'
  | 'inconclusive';

export type ReplayResult = 'pass' | 'fail' | 'inconclusive';

export interface ReplayVerdict {
  result: ReplayResult;
  reasons: string[];
}

export interface ReplayJudgeOutput {
  qualityConfigId?: string;
  quality: {
    flags: Record<string, unknown>;
    data: Record<string, unknown>;
    reasoning: string | null;
  } | null;
  reviewConfigId?: string;
  review: unknown | null;
  verdict: ReplayVerdict;
  endedBy: string;
  /** Present on scenario test cases (kind = 'scenario'); see ScenarioJudgeOutput. */
  scenarioName?: string;
  checks?: ScenarioJudgeOutput['checks'];
  rules?: ScenarioJudgeOutput['rules'];
}

export interface Replay {
  id: string;
  run_id: string | null;
  finding_id: string | null;
  source_call_id: string | null;
  kind: string;
  iteration: number;
  channel: string;
  modality: 'VOICE' | 'TEXT';
  commit_id: string | null;
  scenario: Record<string, unknown>;
  status: ReplayStatus;
  test_call_id: string | null;
  contact_clone_id: string | null;
  result: ReplayResult | null;
  judge_output: ReplayJudgeOutput | null;
  intercepted_effects: unknown[];
  turns: number;
  error: string | null;
  created_by: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateReplayInput {
  source_call_id: string;
  commit_id?: string | null;
  modality?: 'VOICE' | 'TEXT';
}

export const replaysApi = {
  list: async (sourceCallId?: string) =>
    (
      await apiClient.get<Replay[]>('/agent-improvement/replays', {
        params: sourceCallId ? { source_call_id: sourceCallId } : undefined,
      })
    ).data,

  get: async (id: string) => (await apiClient.get<Replay>(`/agent-improvement/replays/${id}`)).data,

  create: async (input: CreateReplayInput) =>
    (await apiClient.post<Replay>('/agent-improvement/replays', input)).data,

  cancel: async (id: string) =>
    (await apiClient.post<Replay>(`/agent-improvement/replays/${id}/cancel`)).data,
};
